<template>
  <SoDataTable
    v-if="tableItems.length > 0 && !isCustomerSettingsLoading"
    :headers="headers"
    :items="tableItems"
    :searchable="false"
    :has-pagination="false"
    actions-header-text=""
    class="mb-6"
    data-testid="psr-modal-tab-customer-vulnerabilities"
  >
    <template #item.vulnerability="{ internalItem }">
      <div>
        {{ internalItem.raw.vulnerability }}
        <div
          v-if="internalItem.raw.note"
          class="text-caption text-medium-emphasis"
        >
          {{ internalItem.raw.note }}
        </div>
      </div>
    </template>
    <template #itemActions="{ item }">
      <SoButton
        v-if="item.isDeleted"
        title="Vulnerability will be removed"
        type="danger"
        @click="rollbackVulnerability(item)"
      />
      <SoButton
        v-else
        title="Remove vulnerability"
        type="table-action"
        @click="removeVulnerability(item)"
      />
    </template>
  </SoDataTable>
  <SoStatus
    v-else
    text="No Active Vulnerability"
    class="mr-2"
    data-testid="no-vulnerabilities"
  />

  <SoForm v-model="isValid">
    <section v-for="(select, index) in selects" :key="index">
      <div class="d-flex align-start ga-2">
        <SoSelect
          v-model="selects[index]"
          :items="getAvailableItems(selects[index])"
          class="mt-8 w-75"
          label="Choose vulnerability to add"
          :rules="[rules.required]"
          data-testid="psr-vulnerabilities-select"
          @update:model-value="handleSelectUpdate(index)"
        />

        <SoDatePicker
          v-if="shouldShowTemporarySelect(selects[index])"
          v-model="temporaryVulnerabilities[index]"
          label="End date"
          class="flex-shrink-0"
          style="margin-top: 32px"
          :error="errors[index]"
        />

        <SoTextField
          v-if="isEnglishAsSecondLanguage(selects[index])"
          v-model="languageNotes[index]"
          label="Language spoken"
          class="flex-shrink-0"
          style="margin-top: 32px"
          :rules="[rules.required]"
          data-testid="psr-vulnerability-language-input"
        />

        <SoButton
          type="icon"
          icon="close"
          class="mt-16 flex-shrink-0"
          @click="removeVulnerabilitySelect(index)"
        />
      </div>
    </section>

    <SoButton
      data-testid="psr-modal-tab-add-vulnerability"
      title="Add vulnerability"
      type="secondary"
      prepend-icon="plus"
      large
      class="mt-4"
      @click="addVulnerabilitySelect()"
    />
    <SoAlert
      v-if="selects.length > 0"
      class="mt-5"
      type="warning"
      text="Tell the customer: We will share this information with the network operator."
    />

    <div class="d-flex align-center justify-end mt-8 ga-2">
      <SoButton
        title="cancel"
        type="secondary"
        large
        @click="$emit('cancel')"
      />
      <SoButton
        title="continue"
        :disabled="!isValid"
        type="success"
        large
        submit
        @click="handleSubmit()"
      />
    </div>
  </SoForm>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import SoStatus from "@/components/SoStatus.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import {
    SoDataTableColumnType,
    SoDataTableItem,
  } from "@/types/components/SoDataTable"
  import { SoSelectItem, SoSelectValue } from "@/types/components/SoSelect"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import rules from "@/validators/rules"
  import SoDatePicker from "@/components/formControls/SoDatePicker.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import dayjs, { Dayjs } from "dayjs"
  import { IStorePsrCustomerVulnerability } from "@/mutators/psr/PsrMutator.types"
  import { formatDateDashed } from "@/helpers/dateHelper"
  import {
    ENGLISH_AS_SECOND_LANGUAGE_LABEL,
    IPsrCustomerVulnerability,
  } from "@/models/psr/psr.types"

  interface SoDataTableVulnerabilityItem extends SoDataTableItem {
    vulnerability?: string
    fromDt: Dayjs
    toDt: Dayjs | null
    id: number
    isDeleted: boolean
    note?: string
  }
  export default defineComponent({
    components: {
      SoAlert,
      SoStatus,
      SoDataTable,
      SoSelect,
      SoButton,
      SoForm,
      SoDatePicker,
      SoTextField,
    },
    props: {
      isServicesTabClicked: {
        type: Boolean,
        default: false,
        required: true,
      },
    },

    emits: ["cancel", "submit", "isValid"],
    data() {
      return {
        current: useCurrentAccountStore(),
        headers: [
          { key: "vulnerability", title: "Reason" },
          {
            key: "fromDt",
            title: "From",
            type: SoDataTableColumnType.DATE,
          },
          {
            key: "toDt",
            title: "To",
            type: SoDataTableColumnType.DATE,
          },
        ],
        isValid: true,
        selects: [] as (number | null)[],
        temporaryVulnerabilities: [] as (Date | null)[],
        languageNotes: [] as (string | null)[],
        rules,
        isSubmitted: false,
      }
    },
    computed: {
      isCustomerSettingsLoading(): boolean {
        return this.current.customerSettings.isFetching
      },
      tableItems(): SoDataTableVulnerabilityItem[] {
        const vulnerabilities =
          this.current.customerSettings.data?.customerVulnerabilities ?? []
        return vulnerabilities.map((v: IPsrCustomerVulnerability) => {
          const item: SoDataTableVulnerabilityItem = {
            vulnerability: v.label,
            fromDt: dayjs(v.startDate),
            toDt: v.endDate ? dayjs(v.endDate) : null,
            id: v.id,
            isDeleted: false,
            note: v.note,
          }
          return item
        })
      },
      errors(): string[] {
        return this.temporaryVulnerabilities.map((dateValue, index) => {
          if (!this.shouldShowTemporarySelect(this.selects[index])) {
            return ""
          }
          if (dateValue && dayjs(dateValue).isBefore(dayjs()))
            return "The date is in the past"
          return !dateValue && this.isSubmitted ? "Field is required!" : ""
        })
      },
      endDateNotValid(): boolean {
        const vulnerabilities = this.current.allVulnerabilities.data

        const missingEndDate = this.selects.some((vulId, idx) => {
          if (
            vulId &&
            vulnerabilities.find((vlnrblt) => vlnrblt.id === vulId)
              ?.requiresEndDate
          ) {
            return !this.temporaryVulnerabilities[idx]
          }
        })

        const dateIsInThePast = this.temporaryVulnerabilities.some(
          (dateValue) => dateValue && dayjs(dateValue).isBefore(dayjs()),
        )

        return missingEndDate || dateIsInThePast
      },
    },
    watch: {
      // we need to watch both isValid and endDateNotValid
      // because isValid is v-model of SoForm
      // when we check Dates separately passing errors array as attribute of
      // SoDatePicker
      isValid(value: boolean | null) {
        const valid = value && !this.endDateNotValid
        this.$emit("isValid", valid)
      },
      endDateNotValid(value: boolean) {
        const valid = !value && this.isValid
        this.$emit("isValid", valid)
      },
      isServicesTabClicked: {
        immediate: true,
        async handler(value: boolean) {
          if (value) {
            this.handleSubmit()
          }
        },
      },
    },
    methods: {
      getAvailableItems(selectValue: SoSelectValue): SoSelectItem[] {
        return this.current.allVulnerabilities.data
          .filter(
            (v) =>
              v.id === selectValue ||
              (!this.selects.includes(v.id) &&
                !this.tableItems.some((item) => item.id === v.id)),
          )
          .map((v) => ({
            title: v.label,
            value: v.id,
          }))
      },
      addVulnerabilitySelect() {
        this.isSubmitted = false
        this.selects.push(null)
        this.temporaryVulnerabilities.push(null)
        this.languageNotes.push(null)
      },
      removeVulnerabilitySelect(position: number) {
        this.isSubmitted = false
        this.selects = this.selects.filter((_, index) => index !== position)
        this.temporaryVulnerabilities.splice(position, 1)
        this.languageNotes.splice(position, 1)
      },
      shouldShowTemporarySelect(selectValue: SoSelectValue | null) {
        return this.current.allVulnerabilities.data.some(
          (v) => v.requiresEndDate && v.id === selectValue,
        )
      },
      isEnglishAsSecondLanguage(selectValue: SoSelectValue | null): boolean {
        return this.current.allVulnerabilities.data.some(
          (v) =>
            v.id === selectValue &&
            v.label === ENGLISH_AS_SECOND_LANGUAGE_LABEL,
        )
      },
      removeVulnerability(item: SoDataTableItem) {
        item.isDeleted = true
      },
      rollbackVulnerability(item: SoDataTableItem) {
        item.isDeleted = false
      },

      handleSubmit() {
        this.isSubmitted = true
        if (this.errors.some((error) => error !== "")) {
          return
        }
        const tableVulnerabilities: IStorePsrCustomerVulnerability[] =
          this.tableItems
            .filter(
              (tableItem: SoDataTableVulnerabilityItem) => !tableItem.isDeleted,
            )
            .map((tableItem: SoDataTableVulnerabilityItem) => {
              return {
                id: tableItem.id,
                startDate: formatDateDashed(dayjs()),
                endDate: tableItem.toDt
                  ? formatDateDashed(tableItem.toDt)
                  : undefined,
                note: tableItem.note,
              }
            })
        const notNullSelects: IStorePsrCustomerVulnerability[] =
          this.selects.map((select: number | null, index: number) => {
            if (select === null) {
              throw new Error("All selects should be populated by this point")
            }
            const vulnerabilityEndDate = this.temporaryVulnerabilities[index]
            return {
              id: select,
              startDate: formatDateDashed(dayjs()),
              endDate: vulnerabilityEndDate
                ? formatDateDashed(dayjs(vulnerabilityEndDate))
                : undefined,
              note: this.languageNotes[index] ?? undefined,
            }
          })

        const vulnerabilities: Array<IStorePsrCustomerVulnerability> = [
          ...tableVulnerabilities,
          ...notNullSelects,
        ]
        this.current.setVulnerabilities(vulnerabilities)
        this.$emit("submit")
      },
      handleSelectUpdate(index: number) {
        this.isSubmitted = false
        if (!this.isEnglishAsSecondLanguage(this.selects[index])) {
          this.languageNotes[index] = null
        }
      },
    },
  })
</script>
